import { StockClient } from "../clients/StockClient";
import { Stock } from "@models/Stock";
import { supabase } from "@supabaseClient/supabaseClient";
import { create } from "zustand";
import { queryClient } from "src/lib/queryClient";

// Bridge: stock reads have started migrating to TanStack (e.g.
// `useStocksByLocation` for the inventory hub). Until writes also move,
// this Zustand store is the source of truth for mutations — we invalidate
// the TanStack ['stocks'] cache after each one so the new readers stay
// fresh. Delete this bridge when stock mutations land in TanStack.
const invalidateStocksCache = () => {
  queryClient.invalidateQueries({ queryKey: ["stocks"] });
};

export type StockStoreState = {
  stocks: Stock[];
  loading: boolean;
  error: Error | null;
  getStocksByOrganizationId: (
    organization_id: string,
    useLoading?: boolean
  ) => Promise<void>;
  createStock: (
    stock: Omit<Stock, "id" | "created_at" | "updated_at">
  ) => Promise<void>;
  updateStock: (stock: Omit<Stock, "created_at" | "updated_at">) => Promise<void>;
  moveStock: (
    source: Stock,
    targetLocationId: string
  ) => Promise<{ merged: boolean }>;
  deleteStock: (id: string) => Promise<void>;
  refreshStock: (inventoryId: string) => Promise<void>;
  refreshStocksByProductId: (productId: string) => Promise<void>;
};

export const useStockStore = create<StockStoreState>((set) => ({
  stocks: [],
  loading: true,
  error: null,
  async getStocksByOrganizationId(organization_id: string, useLoading = true) {
    if (useLoading) {
      set({ loading: true });
    }
    try {
      const stocks = await StockClient.getStockByOrganizationId(
        organization_id
      );
      set({ stocks });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
  async createStock(stock: Omit<Stock, "id" | "created_at" | "updated_at">) {
    set({ loading: true });
    try {
      const newStock = await StockClient.createStock(stock);
      set((state) => ({ stocks: [...state.stocks, newStock] }));
      invalidateStocksCache();

      // Process backorders for this product
      try {
        const { StockCommitmentsClient } = await import(
          "../clients/StockCommitmentsClient"
        );
        await StockCommitmentsClient.processBackordersForProduct(
          stock.product_id
        );
        console.log(`Backorders processed for product: ${stock.product_id}`);
      } catch (backorderError) {
        console.error("Error processing backorders:", backorderError);
        // Don't fail the stock creation if backorder processing fails
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
  async updateStock(stock: Omit<Stock, "created_at" | "updated_at">) {
    set({ loading: true });
    try {
      const updatedStock = await StockClient.updateStock(stock);
      set((state) => ({
        stocks: state.stocks.map((s) =>
          s.id === updatedStock.id ? updatedStock : s
        ),
      }));
      invalidateStocksCache();

      // Process backorders for this product
      try {
        const { StockCommitmentsClient } = await import(
          "../clients/StockCommitmentsClient"
        );
        await StockCommitmentsClient.processBackordersForProduct(
          stock.product_id
        );
        console.log(`Backorders processed for product: ${stock.product_id}`);
      } catch (backorderError) {
        console.error("Error processing backorders:", backorderError);
        // Don't fail the stock update if backorder processing fails
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
  async moveStock(source: Stock, targetLocationId: string) {
    set({ loading: true });
    try {
      const result = await StockClient.moveStock(source, targetLocationId);
      // A merge changes the destination's total too, so the simplest correct
      // mirror is to drop the source here and let readers refetch the fresh
      // numbers; a plain move just repoints the row.
      set((state) => ({
        stocks: result.merged
          ? state.stocks.filter((s) => s.id !== source.id)
          : state.stocks.map((s) =>
              s.id === source.id
                ? { ...s, location_id: targetLocationId }
                : s
            ),
      }));
      invalidateStocksCache();
      return result;
    } catch (error) {
      // Unlike the other writes here, rethrow so the caller can surface the
      // failure and keep its dialog open instead of showing a false success.
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  async deleteStock(id: string) {
    set({ loading: true });
    try {
      await StockClient.deleteStock(id);
      set((state) => ({ stocks: state.stocks.filter((s) => s.id !== id) }));
      invalidateStocksCache();
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
  async refreshStock(inventoryId: string) {
    try {
      const { data, error } = await supabase
        .from("inventory")
        .select("*")
        .eq("id", inventoryId)
        .single();

      if (error) {
        throw error;
      }

      set((state) => ({
        stocks: state.stocks.map((s) => (s.id === inventoryId ? data : s)),
      }));
    } catch (error) {
      console.error("Error refreshing stock:", error);
    }
  },
  async refreshStocksByProductId(productId: string) {
    try {
      const stocks = await StockClient.getInventoryByProductId(productId);
      set((state) => ({
        stocks: [
          ...state.stocks.filter((s) => s.product_id !== productId),
          ...stocks,
        ],
      }));
    } catch (error) {
      console.error("Error refreshing stocks by product ID:", error);
    }
  },
}));
